/**
 * PnLService
 *
 * RESPONSIBILITIES:
 * - Centralized PnL calculations for positions and portfolios
 * - Consistent position metrics calculation across the application
 * - Portfolio aggregation and total value calculations
 * - Current value, cost basis, and profit/loss computations
 *
 * SHOULD NOT:
 * - Calculate option prices or volatility (delegates to OrderService/VolatilityService)
 * - Handle position management or account balances
 * - Perform order processing or execution
 * - Store or persist position data
 */

import type { Position } from '@shared/types';

import { OrderService } from './orderService';
import { priceService } from './priceService';
import { volatilityService } from './volatilityService';
export class PnLService {
  /**
   * Calculate PnL for a single position using consistent methodology
   */
  static calculatePositionPnL(
    position: Position,
    spotPrice: number,
    volatility: number,
    currentTime: number = Date.now(),
  ): {
    currentValue: number;
    totalCost: number;
    pnlTotal: number;
    pnlPerContract: number;
    roi: number;
  } {
    if (!volatility || volatility <= 0) {
      throw new Error(
        `Invalid volatility for ${position.asset}: ${volatility}. Cannot calculate PnL without valid volatility data.`,
      );
    }

    if (!position.strikePrice || !position.quantity) {
      throw new Error(
        `Invalid position data: missing required fields. Position: ${JSON.stringify(position)}`,
      );
    }

    const totalCost = position.purchasePrice * position.quantity;

    // Handle 0 spot price gracefully - position has no current market value
    if (spotPrice === 0) {
      return {
        currentValue: 0,
        totalCost,
        pnlTotal: -totalCost, // Full loss when no market value
        pnlPerContract: -position.purchasePrice,
        roi: -100, // 100% loss
      };
    }

    // Handle expired positions - calculate intrinsic value
    if (position.expiryTimestamp <= currentTime) {
      const intrinsicValuePerContract =
        position.type === 'CALL'
          ? Math.max(spotPrice - position.strikePrice, 0)
          : Math.max(position.strikePrice - spotPrice, 0);

      const currentValue = intrinsicValuePerContract * position.quantity;
      const pnlTotal = currentValue - totalCost;
      const pnlPerContract = intrinsicValuePerContract - position.purchasePrice;
      const roi = totalCost > 0 ? (pnlTotal / totalCost) * 100 : 0;

      return {
        currentValue,
        totalCost,
        pnlTotal,
        pnlPerContract,
        roi,
      };
    }

    // Use OrderService as the single source of truth for option pricing (live positions only)
    const orderService = OrderService.getInstance();
    const bidAskPricing = orderService.calculatePricing(
      position.asset,
      position.strikePrice,
      position.type,
      position.expiryTimestamp,
    );

    if (!bidAskPricing) {
      throw new Error(`Could not get bid/ask pricing for ${position.asset}`);
    }

    const bidPrice = bidAskPricing.bid;
    const currentValue = bidPrice * position.quantity;
    const pnlTotal = currentValue - totalCost;
    const pnlPerContract = bidPrice - position.purchasePrice;
    const roi = totalCost > 0 ? (pnlTotal / totalCost) * 100 : 0;

    return {
      currentValue,
      totalCost,
      pnlTotal,
      pnlPerContract,
      roi,
    };
  }

  /**
   * Calculate aggregate PnL for multiple positions
   */
  static calculatePortfolioPnL(
    positions: Position[],
    currentTime: number = Date.now(),
  ): {
    totalValue: number;
    totalCost: number;
    totalPnL: number;
    positions: Array<{
      position: Position;
      pnl: null | ReturnType<typeof PnLService.calculatePositionPnL>;
    }>;
  } {
    let totalValue = 0;
    let totalCost = 0;
    let totalPnL = 0;

    const positionResults = positions.map(position => {
      const spotPrice = priceService.getPrice(position.asset)?.price;
      const volatility = volatilityService.getVolatility(position.asset);
      if (!spotPrice || !volatility) {
        return { position, pnl: null };
      }

      const pnl = this.calculatePositionPnL(
        position,
        spotPrice,
        volatility,
        currentTime,
      );

      totalValue += pnl.currentValue;
      totalCost += pnl.totalCost;
      totalPnL += pnl.pnlTotal;

      return { position, pnl };
    });

    return {
      totalValue,
      totalCost,
      totalPnL,
      positions: positionResults,
    };
  }

  /**
   * Simple aggregation for when you just need total PnL
   */
  static calculateTotalPnL(
    positions: Position[],
    spotPrices: Record<string, number>,
    volatilities: Record<string, number>,
    currentTime: number = Date.now(),
  ): number {
    return positions.reduce((total, position) => {
      const spotPrice = spotPrices[position.asset];
      const volatility = volatilities[position.asset];

      // Use default volatility if data is insufficient (volatility service returns 0)
      const effectiveVolatility = volatility || 0.5; // 50% default for missing data

      // Silently use default volatility when data is insufficient

      const pnl = this.calculatePositionPnL(
        position,
        spotPrice,
        effectiveVolatility,
        currentTime,
      );

      return total + pnl.pnlTotal;
    }, 0);
  }
}
